import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import axios from 'axios';
import { Check, Copy, Download, ShieldAlert } from 'lucide-react';
import { getApiBaseUrl } from '../../services/api';
import { useActiveClient } from '../../contexts/ActiveClientContext';
import { Action, HelpToggle, Panel, PanelHead } from '../../components/aa';
import { Field } from './settingsSections';

/*
 * The Solution Partner's scoping plugin (Orbit). Two ways in: the remote
 * connector address to paste into Claude, which carries no credential, and a
 * personal local download for Claude Code, which does. The download can be
 * pinned to one client so every session lands on that client's dossier.
 *
 * Whether this partner may download at all is a platform setting the server
 * checks on every download; the section asks once so it can say so up front.
 */

const MCP_URL = `${(process.env.REACT_APP_BUILDER_URL || 'https://builder.agentsanywhere.ai').replace(/\/+$/, '')}/api/mcp/http`;
const NOT_ENABLED = 'Orbit downloads are not enabled for your account. Ask the platform team to switch them on.';

const failureMessage = async (err: unknown): Promise<string> => {
  const response = (err as { response?: { data?: unknown } }).response;
  const data = response?.data;
  if (data instanceof Blob) {
    try {
      const parsed = JSON.parse(await data.text()) as { detail?: string };
      if (parsed.detail) return parsed.detail;
    } catch {
      return 'The download failed.';
    }
  }
  if (data && typeof data === 'object' && 'detail' in data) {
    return String((data as { detail?: string }).detail || 'The download failed.');
  }
  return 'The download failed. Check that the builder is reachable.';
};

type PinnableClient = { id: string; name: string };

/** The two panels, for any shell that knows its own clients. */
export const ScopingPluginPanels: React.FC<{ clients: PinnableClient[] }> = ({ clients }) => {
  const [allowed, setAllowed] = useState<boolean | null>(null);
  const [copied, setCopied] = useState(false);
  const [pin, setPin] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get<{ allowed: boolean }>(`${getApiBaseUrl()}/plugin/access`)
      .then((res) => setAllowed(Boolean(res.data.allowed)))
      .catch(() => setAllowed(false));
  }, []);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(MCP_URL);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.info('Select the address and copy it yourself.');
    }
  };

  const download = async () => {
    setBusy(true);
    try {
      let url = `${getApiBaseUrl()}/plugin/download`;
      if (pin) url = `${url}?client_id=${encodeURIComponent(pin)}`;
      const res = await axios.get(url, { responseType: 'blob' });
      const disposition = res.headers['content-disposition'] || '';
      const named = disposition.match(/filename="?([^";]+)"?/);
      const href = URL.createObjectURL(res.data);
      const link = document.createElement('a');
      link.href = href;
      link.download = named?.[1] || 'orbit-scope.zip';
      link.click();
      URL.revokeObjectURL(href);
      toast.success('Downloaded. This copy holds your fresh credential; any earlier download is now revoked.');
    } catch (err) {
      toast.error(await failureMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const pinned = clients.find((c) => c.id === pin);

  const copyIcon = () => {
    if (copied) return <Check size={14} strokeWidth={2.5} />;
    return <Copy size={14} />;
  };
  const copyLabel = () => {
    if (copied) return 'Copied';
    return 'Copy';
  };
  const downloadLabel = () => {
    if (pinned) return `Download plugin for ${pinned.name}`;
    return 'Download plugin';
  };

  return (
    <div className="space-y-5">
      <Panel>
        <PanelHead
          kicker="Use it inside Claude"
          title="Nothing to download"
          desc="Add this address in Claude, sign in with this account when it asks, and your scoping tools are there. Works in Claude on the web, desktop and Claude Code."
        />
        <div className="px-5 pb-5 md:px-6">
          <div className="flex flex-wrap items-center gap-2">
            <code className="min-w-0 flex-1 truncate rounded-act border border-line-strong bg-canvas-inset px-3 py-2.5 font-sans text-[12.5px] text-ink">
              {MCP_URL}
            </code>
            <Action variant="quiet" onClick={copy} icon={copyIcon()}>{copyLabel()}</Action>
          </div>
          <HelpToggle label="How to connect" className="mt-3">
            <ol className="m-0 list-decimal space-y-1 pl-5 text-[13px] leading-[21px] text-ink-dim">
              <li>In Claude: Settings, Connectors, Add custom connector</li>
              <li>Paste the address above and save</li>
              <li>Claude sends you here to sign in and approve, then you are connected</li>
            </ol>
            <p className="m-0 mt-3 text-[12.5px] text-ink-soft">
              No key to copy or keep safe. Approve once; you can disconnect from Claude at any time.
            </p>
          </HelpToggle>
        </div>
      </Panel>

      <Panel>
        <PanelHead
          kicker="Prefer a local install?"
          title="Download the plugin for Claude Code"
          desc="The download is personal: it bakes in your scoping credential, and the plugin asks which client each session is for before anything is saved."
        />
        <div className="space-y-5 px-5 pb-5 md:px-6">
          <HelpToggle label="Safety notes">
            <div className="rounded-panel border border-warn/40 bg-warn-soft px-4 py-3.5">
              <div className="flex items-center gap-2 text-[12.5px] font-semibold text-ink">
                <ShieldAlert size={14} /> Safety
              </div>
              <ul className="m-0 mt-1.5 list-disc space-y-1 pl-5 text-[12.5px] leading-[20px] text-ink-dim">
                <li>The zip contains a live credential. Never share, email, or commit it.</li>
                <li>Downloading again issues a fresh credential and revokes the previous one.</li>
                <li>Confirm which client a session is for before sharing client information.</li>
              </ul>
            </div>
          </HelpToggle>

          {allowed === false && (
            <p className="m-0 text-[13px] text-ink-dim">{NOT_ENABLED}</p>
          )}

          {allowed && (
            <>
              <Field label="Download for a specific client (optional)" hint="A pinned download skips the which-client step. Every session lands on this client's dossier, and the credential cannot touch any other client.">
                <select
                  value={pin}
                  onChange={(e) => setPin(e.target.value)}
                  className="h-11 w-full max-w-[520px] rounded-act border border-line-strong bg-white px-3 text-[13px] text-ink"
                >
                  <option value="">All clients: Orbit asks which client each session</option>
                  {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </Field>
              <div className="flex flex-wrap items-center gap-3">
                <Action icon={<Download size={14} />} onClick={download} disabled={busy} reason="Preparing your plugin">
                  {downloadLabel()}
                </Action>
                <span className="text-[12.5px] text-ink-soft">Setup steps and safety notes are inside the zip.</span>
              </div>
            </>
          )}
        </div>
      </Panel>
    </div>
  );
};

export const ScopingPluginSection: React.FC = () => {
  const { clients } = useActiveClient();
  return <ScopingPluginPanels clients={clients} />;
};
